import MoneyInput from "@common/components/form/MoneyInput";
import { onSubmitError, vehicleValidation } from "@talladega/helpers";
import { useNumberMask } from "@common/hooks/useMask";
import { NameId } from "@common/types/NameId";
import { LocalDate, convert, nativeJs } from "@js-joda/core";
import { useRouter } from "next/router";
import { useState, useEffect, ChangeEvent } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useTranslation } from "next-i18next";
import {
  DatePicker,
  Form,
  Listbox,
  MakeInput,
  ModelInput,
  SubStepButtons,
  TrimInput,
  YearInput,
} from "@talladega/components";

import { VinDecodeResponse } from "@modules/vehicle/types/VinDecodeResponse";
import {
  VehicleInformationFieldsInput,
  VehicleInformationFormInput,
} from "../types/BillOfSaleFormFields";

type Props = {
  onSubmit: (formData: VehicleInformationFieldsInput) => void;
  vehicle: VehicleInformationFieldsInput;
  vinDecodedInfo?: VinDecodeResponse;
  onBack: () => void;
  onSubmitValidate: () => void;
};

const focusOrder = [
  "vehicleInformation.year",
  "vehicleInformation.make",
  "vehicleInformation.model",
  "vehicleInformation.trimNameId",
  "vehicleInformation.trim",
  "vehicleInformation.bodyTypeNameId",
  "vehicleInformation.bodyType",
  "vehicleInformation.exteriorColour",
  "vehicleInformation.fuelTypeNameId",
  "vehicleInformation.fuelType",
  "vehicleInformation.odometer",
  "vehicleInformation.sellingPrice",
  "vehicleInformation.vehicleSoldDate",
];

const today = LocalDate.now();
const minDate = convert(today.minusYears(10)).toDate();
const maxDate = convert(today.plusYears(10)).toDate();

const VehicleInformationForm = ({
  onSubmit,
  vehicle,
  vinDecodedInfo,
  onBack,
  onSubmitValidate,
}: Props) => {
  const { t } = useTranslation(["billOfSalePage", "common"]);
  const { maskPipe: maskNumberPipe, unMaskPipe: unMaskNumberPipe } =
    useNumberMask();
  const [selectedTrim, setSelectedTrim] = useState<NameId | undefined>(
    vehicle.trimNameId
  );
  const [selectedBody, setSelectedBody] = useState<NameId | undefined>(
    vehicle.bodyTypeNameId
  );
  const [selectedFuelType, setSelectedFuelType] = useState<NameId | undefined>(
    vehicle.fuelTypeNameId
  );
  const [selectedVehicleSoldDate, setSelectedVehicleSoldDate] =
    useState<Date | null>(() => {
      const { vehicleSoldDate } = vehicle;
      return vehicleSoldDate ? convert(vehicleSoldDate).toDate() : null;
    });
  const [sellingPriceNumeric, setSellingPriceNumeric] = useState<number>(
    unMaskNumberPipe(vehicle.sellingPrice ?? "")
  );

  const [odometerNumeric, setOdometerNumeric] = useState<number>(
    unMaskNumberPipe(vehicle.odometer ?? "")
  );

  const router = useRouter();
  const locale = router.locale || "en";

  const vehicleInformationForm = useForm<VehicleInformationFormInput>({
    values: {
      vehicleInformation: { ...vehicle },
    },
    shouldFocusError: false,
  });

  const {
    handleSubmit,
    register,
    control,
    setValue,
    getValues,
    formState: { isSubmitting, errors },
  } = vehicleInformationForm;

  useEffect(() => {
    if (!vinDecodedInfo) return;

    setSelectedTrim(vehicle.trimNameId);
    setSelectedBody(vehicle.bodyTypeNameId);
    setSelectedFuelType(vehicle.fuelTypeNameId);
    setSelectedVehicleSoldDate(
      vehicle.vehicleSoldDate ? convert(vehicle.vehicleSoldDate).toDate() : null
    );

    // If there is a valid change in VIN
    if (!vehicle.odometer) {
      setOdometerNumeric(0);
    }

    if (!vehicle.sellingPrice) {
      setSellingPriceNumeric(0);
    }

    setValue("vehicleInformation", vehicle);
  }, [setValue, vehicle, vinDecodedInfo]);

  useEffect(() => {
    if (sellingPriceNumeric && sellingPriceNumeric > 0) {
      const masked = maskNumberPipe(sellingPriceNumeric.toString(), locale);
      setValue("vehicleInformation.sellingPrice", masked);
    } else {
      setValue("vehicleInformation.sellingPrice", "");
    }

    if (odometerNumeric && odometerNumeric > 0) {
      const masked = maskNumberPipe(odometerNumeric.toString(), locale);
      setValue("vehicleInformation.odometer", masked);
    } else {
      setValue("vehicleInformation.odometer", "");
    }
  }, [
    getValues,
    locale,
    maskNumberPipe,
    odometerNumeric,
    sellingPriceNumeric,
    setValue,
  ]);

  const onFormSubmit: SubmitHandler<VehicleInformationFormInput> = (
    vehicleInformationInput: VehicleInformationFormInput
  ) => {
    const { vehicleInformation } = vehicleInformationInput;
    const data = {
      ...vehicleInformation,
      trim: vehicleInformation.trimNameId?.name ?? vehicleInformation.trim,
      bodyType:
        vehicleInformation.bodyTypeNameId?.name ?? vehicleInformation.bodyType,
      fuelType:
        vehicleInformation.fuelTypeNameId?.name ?? vehicleInformation.fuelType,
    };

    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit, onSubmitError(focusOrder))}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4">
        <YearInput
          className="w-full"
          name="vehicleInformation.year"
          register={register}
          errors={errors}
          required
        />
        <MakeInput
          className="w-full"
          name="vehicleInformation.make"
          register={register}
          errors={errors}
          required
        />
        <ModelInput
          className="w-full"
          name="vehicleInformation.model"
          register={register}
          errors={errors}
          required
          defaultValue={vehicle.model ?? ""}
        />

        <div className="w-full">
          {vinDecodedInfo?.trims && vinDecodedInfo.trims.length > 1 ? (
            <Controller
              name="vehicleInformation.trimNameId"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("common:required"),
                },
              }}
              defaultValue={vehicle.trimNameId}
              render={({ field: { onChange, ref } }) => (
                <Listbox
                  label={t("common:trim")}
                  items={vinDecodedInfo.trims as NameId[]}
                  keyOption={(item) => item.id}
                  displayOption={(item) => item?.name || ""}
                  displayButton={(item) =>
                    item?.name || t("common:please_select_one") || ""
                  }
                  valueOption={(item) => item}
                  feedback={errors.vehicleInformation?.trimNameId?.message}
                  status={
                    errors.vehicleInformation?.trimNameId ? "error" : "default"
                  }
                  onChange={(selected) => {
                    setSelectedTrim(selected);
                    onChange(selected);
                  }}
                  selectedItem={selectedTrim}
                  buttonRef={ref}
                />
              )}
            />
          ) : (
            <TrimInput
              name="vehicleInformation.trim"
              register={register}
              errors={errors}
              defaultValue={vehicle.trim ?? ""}
            />
          )}
        </div>

        <div className="w-full">
          {vinDecodedInfo?.bodyTypes && vinDecodedInfo.bodyTypes.length > 1 ? (
            <Controller
              name="vehicleInformation.bodyTypeNameId"
              control={control}
              rules={{
                required: {
                  value: vehicleValidation.bodyType.required,
                  message: t("common:required"),
                },
              }}
              defaultValue={vehicle.bodyTypeNameId}
              render={({ field: { onChange, ref } }) => (
                <Listbox
                  label={t("common:body_type")}
                  items={vinDecodedInfo.bodyTypes as NameId[]}
                  keyOption={(item) => item.id}
                  displayOption={(item) => item?.name || ""}
                  displayButton={(item) =>
                    item?.name || t("common:please_select_one") || ""
                  }
                  valueOption={(item) => item}
                  feedback={errors.vehicleInformation?.bodyTypeNameId?.message}
                  status={
                    errors.vehicleInformation?.bodyTypeNameId
                      ? "error"
                      : "default"
                  }
                  onChange={(selected) => {
                    setSelectedBody(selected);
                    onChange(selected);
                  }}
                  selectedItem={selectedBody}
                  buttonRef={ref}
                />
              )}
            />
          ) : (
            <Form.Input
              id="bodyType"
              label={t("common:body_type")}
              feedback={errors.vehicleInformation?.bodyType?.message}
              status={errors.vehicleInformation?.bodyType ? "error" : "default"}
              defaultValue={vehicle.bodyType ?? ""}
              maxLength={vehicleValidation.bodyType.maxLength}
              {...register("vehicleInformation.bodyType", {
                required: {
                  value: vehicleValidation.bodyType.required,
                  message: t("common:required"),
                },
                maxLength: {
                  value: vehicleValidation.bodyType.maxLength,
                  message: t("common:invalid_body_type"),
                },
              })}
            />
          )}
        </div>

        <Form.Input
          id="exteriorColour"
          className="w-full"
          label={t("common:exterior_colour")}
          feedback={errors.vehicleInformation?.exteriorColour?.message}
          status={
            errors.vehicleInformation?.exteriorColour ? "error" : "default"
          }
          defaultValue={vehicle.exteriorColour ?? ""}
          maxLength={vehicleValidation.exteriorColour.maxLength}
          {...register("vehicleInformation.exteriorColour", {
            required: {
              value: vehicleValidation.exteriorColour.required,
              message: t("common:required"),
            },
            maxLength: {
              value: vehicleValidation.exteriorColour.maxLength,
              message: t("common:invalid_exteriour_colour"),
            },
          })}
        />

        <div className="w-full">
          {vinDecodedInfo?.fuelTypes && vinDecodedInfo.fuelTypes.length > 1 ? (
            <Controller
              name="vehicleInformation.fuelTypeNameId"
              control={control}
              rules={{
                required: {
                  value: vehicleValidation.fuelType.required,
                  message: t("common:required"),
                },
              }}
              defaultValue={vehicle.fuelTypeNameId}
              render={({ field: { onChange, ref } }) => (
                <Listbox
                  label={t("common:fuel_type")}
                  items={vinDecodedInfo.fuelTypes as NameId[]}
                  keyOption={(item) => item.id}
                  displayOption={(item) => item?.name || ""}
                  displayButton={(item) => item?.name || ""}
                  valueOption={(item) => item}
                  feedback={errors.vehicleInformation?.fuelTypeNameId?.message}
                  status={
                    errors.vehicleInformation?.fuelTypeNameId
                      ? "error"
                      : "default"
                  }
                  onChange={(selected) => {
                    setSelectedFuelType(selected);
                    onChange(selected);
                  }}
                  selectedItem={selectedFuelType}
                  buttonRef={ref}
                />
              )}
            />
          ) : (
            <Form.Input
              id="fuelType"
              className="w-full"
              label={t("common:fuel_type")}
              feedback={errors.vehicleInformation?.fuelType?.message}
              defaultValue={vehicle.fuelType ?? ""}
              status={errors.vehicleInformation?.fuelType ? "error" : "default"}
              maxLength={vehicleValidation.fuelType.maxLength}
              {...register("vehicleInformation.fuelType", {
                required: {
                  value: vehicleValidation.fuelType.required,
                  message: t("common:required"),
                },
                maxLength: {
                  value: vehicleValidation.fuelType.maxLength,
                  message: t("common:invalid_fuel_type"),
                },
              })}
            />
          )}
        </div>

        <Form.Input
          id="odometer"
          className="w-full"
          type="text"
          autoComplete="on"
          maxLength={vehicleValidation.odometer.maxLength}
          rightIcon={<span className="mr-2">KM</span>}
          label={t("common:odometer_reading_at_time_of_sale")}
          feedback={errors.vehicleInformation?.odometer?.message}
          defaultValue={vehicle.odometer ?? ""}
          status={errors.vehicleInformation?.odometer ? "error" : "default"}
          {...register("vehicleInformation.odometer", {
            required: {
              value: vehicleValidation.odometer.required,
              message: t("common:required"),
            },
            maxLength: {
              value: vehicleValidation.odometer.maxLength,
              message: t("common:invalid_odometer"),
            },
            onChange: (e: ChangeEvent<HTMLInputElement>) => {
              const unmasked = unMaskNumberPipe(e.target.value);
              setOdometerNumeric(unmasked);
              const masked = maskNumberPipe(unmasked.toString(), locale);
              e.target.value = masked;
            },
          })}
        />

        <MoneyInput
          id="sellingPrice"
          isForm
          maxLength={vehicleValidation.price.maxLength}
          label={t("common:selling_price")}
          locale={locale || ""}
          defaultValue={
            vehicle.sellingPrice
              ? maskNumberPipe(vehicle.sellingPrice.toString(), locale)
              : null
          }
          feedback={errors.vehicleInformation?.sellingPrice?.message}
          status={errors.vehicleInformation?.sellingPrice ? "error" : "default"}
          suppressHotjar
          register={register("vehicleInformation.sellingPrice", {
            required: {
              value: vehicleValidation.price.required,
              message: t("common:required"),
            },
            maxLength: {
              value: vehicleValidation.price.maxLength,
              message: t("common:invalid_selling_price"),
            },
            onChange: (e: ChangeEvent<HTMLInputElement>) => {
              const unmasked = unMaskNumberPipe(e.target.value);
              setSellingPriceNumeric(unmasked);
              const masked = maskNumberPipe(unmasked.toString(), locale);
              e.target.value = masked;
            },
          })}
        />

        <div className="w-full">
          <Controller
            name="vehicleInformation.vehicleSoldDate"
            control={control}
            render={({ field }) => {
              const { onChange, ...fieldProps } = field;
              return (
                <DatePicker
                  selected={selectedVehicleSoldDate}
                  label={t("billOfSalePage:date_vehicle_was_purchased_sold")}
                  suppressHotjar
                  onChange={(date: Date | null, e) => {
                    setSelectedVehicleSoldDate(date);

                    const localDate = date
                      ? LocalDate.from(nativeJs(date))
                      : null;
                    onChange(localDate, e);
                  }}
                  locale={locale}
                  minDate={minDate}
                  maxDate={maxDate}
                  {...fieldProps}
                />
              );
            }}
          />
        </div>
      </div>

      <div className="mt-6">
        <SubStepButtons
          backButton={{ disabled: isSubmitting, onClick: onBack }}
          nextButton={{ disabled: isSubmitting, onClick: onSubmitValidate }}
        />
      </div>
    </form>
  );
};

export default VehicleInformationForm;
